import React, { useEffect } from 'react';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import {
    AnimatedFadeContainer,
    PageBehaviourButton,
    Sizes,
    PrimaryButton,
    useToggle,
    FormattedNumberRaisedSymbol,
    ANALYTICS_KEY, 
    ANALYTICS_ATTRIBUTE_NAME,
} from '@ratehub/base-ui';
import { FormattedPrimeRateMessage } from '@ratehub/mtg-common';

import { RatePropType } from '../definitions/BestMortgageRatesPropType';
import ProductTableProviderCell from './ProductTableProviderCell';
import ProductTableBestMortgageRatesMoreDetails from './ProductTableBestMortgageRatesMoreDetails';
import ProductTableBestMortgageRatesLightbox from './ProductTableBestMortgageRatesLightbox';


function ProductTableBestMortgageRatesTableCell({
    rate,
    onRateClick,
    isMoreDetailsInLightbox,
    className,
    ...otherProps
}) {
    const [ isExpanded, toggleExpanded, setIsExpanded ] = useToggle();

    /* Whenever isMoreDetailsInLightbox changes, we want to collapse
        the "more details" section so we don't end up in a case where
        the user's screen size changes and multiple lightboxes are opened */
    useEffect(() => {
        setIsExpanded(false);
    }, [ isMoreDetailsInLightbox ]);

    return (
        <div
            className={classNames(className, ANALYTICS_ATTRIBUTE_NAME)}
            ref={self => {
                if (self) {
                    self[ANALYTICS_KEY] = rate;
                }
            }}
            {...otherProps}
        >
            <div className="rate-details-container">
                <div className="rate-cta-container">
                    <PageBehaviourButton
                        className="rate-cta"
                        data-name="BMRTable-rate-cta"
                        size="extra-large"
                        onClick={() => onRateClick(rate)}
                    >
                        <FormattedNumberRaisedSymbol
                            value={rate.value / 100}
                            format="percent2"
                            symbolClassName="rh-title-xs"
                        />
                    </PageBehaviourButton>
                    <If condition={rate.primeRate != null}>
                        <span className="rh-text-xs rh-my-0 weight-medium">
                            <FormattedPrimeRateMessage
                                value={rate.value}
                                primeRate={rate.primeRate}
                            />
                        </span>
                    </If>
                </div>
                <ProductTableProviderCell
                    className="provider-details rh-mt-0_25"
                    rate={rate}
                    isLogoWrapped={false}
                    hasLogoBorder={true}
                />

                {/* More details components */}
                <AnimatedFadeContainer
                    isVisible={isExpanded && !isMoreDetailsInLightbox}
                    withColour={false}
                    alwaysRenderChildMarkup={true}
                    className="full-width"
                >
                    <ProductTableBestMortgageRatesMoreDetails
                        regularPayment={rate.regularPayment}
                        lumpSum={rate.lumpSum}
                        rateHold={rate.rateHold}
                        isPreApproval={rate.isPreApproval}
                    />
                </AnimatedFadeContainer>

                <If condition={isExpanded && isMoreDetailsInLightbox}>
                    <ProductTableBestMortgageRatesLightbox
                        rate={rate}
                        onClose={toggleExpanded}
                        onRateClick={onRateClick}
                    />
                </If>

                <PageBehaviourButton
                    onClick={toggleExpanded}
                    shouldLowerCaseMessage={false}
                    size="extra-small"
                    target="blank"
                    data-name="BMRTable-toggle-details"
                    className="full-width rh-mt-0_5"
                >
                    <FormattedMessage
                        id="web-components.ProductTableBestMortgageRatesTableCell.toggleMoreDetails"
                        defaultMessage="{ isExpanded, select, true {Less details -} other {More details +} }"
                        values={{ isExpanded }}
                    />
                </PageBehaviourButton>
            </div>

            {/* Main CTA */}
            <div className="main-cta-container">
                <PrimaryButton
                    size="small"
                    message={(
                        <FormattedMessage
                            id="web-components.ProductTableBestMortgageRatesTableCell.ctaInquire"
                            defaultMessage="Inquire"
                        />
                    )}
                    onClick={() => onRateClick(rate)}
                    data-name="BMRTable-main-cta"
                />
            </div>
        </div>
    );
}

ProductTableBestMortgageRatesTableCell.propTypes = {
    rate: RatePropType.isRequired,
    onRateClick: PropTypes.func.isRequired,
    isMoreDetailsInLightbox: PropTypes.bool,
    className: PropTypes.string,
};

ProductTableBestMortgageRatesTableCell.defaultProps = {
    isMoreDetailsInLightbox: false,
    className: undefined,
};

const StyledProductTableBestMortgageRatesTableCell = styled(ProductTableBestMortgageRatesTableCell)`
    display: flex;
    flex-wrap: wrap;
    column-gap: ${Sizes.SPACING.HALF};
    row-gap: ${Sizes.SPACING.ONE};
    width: 100%;

    > .rate-details-container {
        /* We want this to wrap when it reaches ~200px (12.5rem) in width
            but if we're smaller than that then we should use 100%
            so that we don't get stuck at a max of 12.5rem if our container is smaller */
        flex-basis: min(12.5rem, 100%);
        /* We want to grow twice as much as the CTA button */
        flex-grow: 2;
        flex-shrink: 0;

        display: flex;
        flex-wrap: wrap;
        column-gap: ${Sizes.SPACING.HALF};
        row-gap: ${Sizes.SPACING.QUARTER};
        align-items: flex-start;

        > .rate-cta-container {
            flex: 0 0 ${Sizes.SPACING.SIX};

            /* Setting a min-height here for the case where
                we have a rate + prime. This is to ensure the
                buttons below are aligned for each table cell */
            min-height: 3.5rem;

            > .rate-cta {
                line-height: 1;
            }
        }

        > .provider-details {
            flex: 1 0 ${Sizes.SPACING.SIX};
            align-items: flex-start;

            /* Setting a min-height here for the largest provider
                name (CanWise + its subtitle). This is to ensure the
                buttons below are aligned for each table cell */
            min-height: 3.05rem;
        }

        > .full-width {
            flex: 1 0 100%;
        }
    }

    > .main-cta-container {
        flex: 1 0 5.5rem;
    }
`;

export default StyledProductTableBestMortgageRatesTableCell;
